import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CommitIcon,
  DotsHorizontalIcon,
  CodeIcon,
  Pencil1Icon,
} from '@radix-ui/react-icons'
import { Sparkles } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuSub,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useUpdateDrafts } from 'api/index'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import Loader from '@/components/ui/Loader'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { CopyItem } from 'components/ui/DropdownMenu'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import DataTableRowEditorTags from 'components/template-tags'
import {
  LevelOptions,
  LabelOptions,
  ContentLangs,
  ProductOptions,
  DEFAULT_PRODUCT_IDS,
} from 'config'
import { cn } from '@/lib/utils'

export const DataTableRowActions = ({ row }) => {
  const data = row.original

  return (
    <div className="flex items-center gap-2">
      <Button asChild>
        <Link to={`/review/${data.id}`}>
          <CommitIcon className="w-4 h-4 mr-2" /> <span>Review</span>
        </Link>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <DotsHorizontalIcon className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[200px]">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <CopyItem data={data.id} />
          <CopyItem data={data.shortId}>Copy shortId</CopyItem>
          <DropdownMenuSeparator />
          <DataTableRowUpdateLevel row={row} />
          <DataTableRowUpdateLabels row={row} />
          <DataTableRowEditorTags row={row} useUpdateDrafts={useUpdateDrafts} />
          <DropdownMenuItem asChild>
            <a
              href={`/admin/ops-tools?opsToolsTab=template-tag-recommend&tid=${encodeURIComponent(data.id)}`}
              target="_blank"
              rel="noreferrer"
            >
              <Sparkles className="mr-2 h-4 w-4 shrink-0 fill-amber-300 text-fuchsia-500" />
              AI 打标签
            </a>
          </DropdownMenuItem>
          <DataTableRowEditorLangs row={row} />
          <DataTableRowEditorProducts row={row} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

const DataTableRowUpdateLevel = ({ row }) => {
  const initData = row.original?.ext?.level
  const { handleUpdate, isMutating } = useUpdateDrafts(row.id)

  const handleUpdateLevel = (e, level) => {
    e.preventDefault()
    handleUpdate('level', level, false)
  }
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <CodeIcon className="w-4 h-4 mr-2" /> Set Level
        {isMutating && 'ing...'}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          {LevelOptions.map((level) => (
            <DropdownMenuCheckboxItem
              key={level.name}
              checked={level.code === initData}
              onSelect={(e) => handleUpdateLevel(e, level.code)}
            >
              {level.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

const DataTableRowUpdateLabels = ({ row }) => {
  const initData = row.original?.ext?.labels || []
  const { handleUpdate, isMutating } = useUpdateDrafts(row.id)

  const handleAddLabels = (e, value) => {
    e.preventDefault()
    handleUpdate('addLabel', value, false)
  }
  const handleRemoveLabels = (e, value) => {
    e.preventDefault()
    handleUpdate('removeLabel', value, false)
  }
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <CodeIcon className="w-4 h-4 mr-2" /> Set Labels
        {isMutating && 'ing...'}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          {LabelOptions.map((label) => (
            <DropdownMenuCheckboxItem
              key={label.name}
              checked={initData.includes(label.code)}
              onSelect={(e) =>
                initData.includes(label.code)
                  ? handleRemoveLabels(e, label.code)
                  : handleAddLabels(e, label.code)
              }
            >
              {label.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

const DataTableRowEditorProducts = ({ row }) => {
  const data = row.original
  const curProducts = Array.isArray(data?.ext?.productIds)
    ? data.ext.productIds
    : []
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(curProducts)
  const { handleUpdate, isMutating } = useUpdateDrafts(data.id)

  const handleOpenChange = (next) => {
    if (next) {
      setSelected(Array.isArray(curProducts) ? [...curProducts] : [])
    }
    setOpen(next)
  }

  const toggle = (pid) => {
    setSelected((prev) => {
      if (prev.includes(pid)) {
        return prev.filter((p) => p !== pid)
      }
      return [...prev, pid].sort((a, b) => a - b)
    })
  }

  const selectAll = () => setSelected([...DEFAULT_PRODUCT_IDS])
  const allSelected =
    DEFAULT_PRODUCT_IDS.length > 0 &&
    DEFAULT_PRODUCT_IDS.every((pid) => selected.includes(pid))

  const handleConfirm = async () => {
    await handleUpdate('setProductIds', selected, false)
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger className="w-full">
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <Pencil1Icon className="mr-2 h-4 w-4" /> Edit Products
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>上线产品</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          未设置 = 未上任何产品。提审进 draft 时会默认全选 11 / 12 / 16。
        </p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 border-b pb-2">
            <Checkbox
              id="product-select-all"
              checked={allSelected}
              onCheckedChange={(checked) => {
                if (checked) selectAll()
                else setSelected([])
              }}
              disabled={isMutating}
            />
            <label
              htmlFor="product-select-all"
              className="cursor-pointer text-sm font-medium"
            >
              Select All
            </label>
          </div>
          {ProductOptions.map((item) => {
            const id = `product-${item.code}`
            return (
              <div key={item.code} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={selected.includes(item.code)}
                  onCheckedChange={() => toggle(item.code)}
                  disabled={isMutating}
                />
                <label htmlFor={id} className="cursor-pointer text-sm">
                  {item.name}-{item.code}
                </label>
              </div>
            )
          })}
        </div>
        {isMutating ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader /> Updating...
          </div>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isMutating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isMutating}
          >
            确认修改
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const DataTableRowEditorLangs = ({ row }) => {
  const data = row.original
  const curLangs = data?.ext?.contentLangs || []
  const [open, setOpen] = useState(false)
  const { handleUpdate, isMutating } = useUpdateDrafts(data.id)

  const handleUpdateFn = (value) => {
    if (curLangs.includes(value)) {
      handleUpdate('removeLang', value, false)
    } else {
      handleUpdate('addLang', value, false)
    }
  }

  const handleAllFn = () => {
    const editableLangs = ContentLangs.slice(1).filter((lang) => !lang.readonly)
    if (curLangs.length) {
      editableLangs.map((lang) =>
        handleUpdate('removeLang', lang.code, false),
      )
    } else {
      editableLangs.map((lang) =>
        handleUpdate('addLang', lang.code, false),
      )
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger className="w-full">
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Pencil1Icon className="mr-2 h-4 w-4" /> Edit Langs
          </DropdownMenuItem>
        </DialogTrigger>
        <DialogContent className="max-h-screen overflow-y-auto">
          <DialogHeader className={'flex'}>
            <DialogTitle>Edit Langs</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            特殊国家：99-测试国家，100-故事线国家，101-小屋国家
          </p>
          <div className="d-flex flex-wrap">
            <Badge
              className={'me-1 cursor-pointer'}
              variant={curLangs.length < 44 ? 'outline' : ''}
              onClick={handleAllFn}
            >
              <small>Slelect All</small>
            </Badge>
            {ContentLangs.slice(1).map((item) => (
              <Badge
                key={item.name}
                className={cn(
                  'me-1',
                  item.readonly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                )}
                variant={!curLangs.includes(item.code) ? 'outline' : ''}
                onClick={() => {
                  if (item.readonly) return
                  handleUpdateFn(item.code)
                }}
              >
                <small>
                  {item.name}-{item.code}
                  {item.readonly && ' (auto)'}
                </small>
              </Badge>
            ))}
          </div>
          <div
            className={cn(
              'grid grid-cols-4 items-center gap-4',
              isMutating ? '' : 'invisible',
            )}
          >
            <Label></Label>
            <div className="col-span-3 flex items-center gap-2">
              <Loader /> Updating...
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
